// ===========================================
// Piece Stages Manager Component
// ===========================================
// Manages learning stages (PieceSkill rows) for an existing piece.
// Supports edit name/color, reorder, delete, and per-stage lesson plan
// (pick from Drive or upload a PDF).

import { useRef, useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Button,
  Typography,
  Stack,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Clear as ClearIcon,
  PictureAsPdf as PictureAsPdfIcon,
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import ColorPickerField from '../common/ColorPickerField';
import ConfirmDialog from '../common/ConfirmDialog';
import DriveFilePicker, { SelectedDriveFile } from '../googleDrive/DriveFilePicker';
import {
  usePiece,
  useAddSkill,
  useUpdateSkill,
  useDeleteSkill,
  useReorderSkills,
  useUploadLessonPlan,
  useClearLessonPlan,
} from '../../hooks/useGamification';
import type { PieceSkill } from '../../types/gamification.types';

// ===========================================
// TYPES
// ===========================================

interface PieceStagesManagerProps {
  pieceId: string;
  driveConnected: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function PieceStagesManager({
  pieceId,
  driveConnected,
}: PieceStagesManagerProps) {
  // Always fetch fresh stages so mutations auto-refresh
  const { data: piece, isLoading } = usePiece(pieceId);
  const stages: PieceSkill[] = piece?.skills ?? [];

  const addSkill = useAddSkill();
  const updateSkill = useUpdateSkill();
  const deleteSkill = useDeleteSkill();
  const reorderSkills = useReorderSkills();
  const uploadLessonPlan = useUploadLessonPlan();
  const clearLessonPlan = useClearLessonPlan();

  // Inline edit state: map of skillId → { name, color } pending
  const [editState, setEditState] = useState<Record<string, { name: string; color: string | null }>>({});

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<PieceSkill | null>(null);

  // Drive picker: track which skillId is being targeted
  const [drivePickerSkillId, setDrivePickerSkillId] = useState<string | null>(null);

  // Hidden file input per-stage upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingSkillId, setUploadingSkillId] = useState<string | null>(null);

  // ===========================================
  // HELPERS
  // ===========================================

  function getDraft(stage: PieceSkill) {
    return editState[stage.id] ?? { name: stage.name, color: stage.color };
  }

  function setDraft(skillId: string, patch: Partial<{ name: string; color: string | null }>) {
    setEditState((prev) => ({
      ...prev,
      [skillId]: { ...getDraftById(skillId), ...patch },
    }));
  }

  function getDraftById(skillId: string) {
    const stage = stages.find((s) => s.id === skillId);
    return editState[skillId] ?? { name: stage?.name ?? '', color: stage?.color ?? null };
  }

  async function saveNameColor(stage: PieceSkill) {
    const draft = getDraft(stage);
    // Only persist if something changed
    if (draft.name === stage.name && draft.color === stage.color) return;
    await updateSkill.mutateAsync({
      skillId: stage.id,
      data: { name: draft.name || stage.name, color: draft.color },
    });
    // Clear the local draft now that server is source of truth
    setEditState((prev) => {
      const next = { ...prev };
      delete next[stage.id];
      return next;
    });
  }

  function moveStage(index: number, direction: 'up' | 'down') {
    const sorted = [...stages].sort((a, b) => a.sortOrder - b.sortOrder);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sorted.length) return;
    const newOrder = sorted.map((s) => s.id);
    [newOrder[index], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[index]];
    reorderSkills.mutate({ pieceId, data: { skillIds: newOrder } });
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await deleteSkill.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  }

  async function handleAddStage() {
    await addSkill.mutateAsync({ pieceId, data: { name: `Stage ${stages.length + 1}` } });
  }

  // Drive picker select
  async function handleDriveSelect(file: SelectedDriveFile) {
    if (!drivePickerSkillId) return;
    await updateSkill.mutateAsync({
      skillId: drivePickerSkillId,
      data: { lessonPlanUrl: file.webViewLink, lessonPlanName: file.name, lessonPlanProvider: 'GOOGLE_DRIVE' },
    });
    setDrivePickerSkillId(null);
  }

  // File upload trigger
  function triggerUpload(skillId: string) {
    setUploadingSkillId(skillId);
    fileInputRef.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !uploadingSkillId) return;
    await uploadLessonPlan.mutateAsync({ pieceId, file, skillId: uploadingSkillId });
    setUploadingSkillId(null);
  }

  async function handleClearLessonPlan(skillId: string) {
    await clearLessonPlan.mutateAsync({ pieceId, skillId });
  }

  // ===========================================
  // RENDER
  // ===========================================

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const sorted = [...stages].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {sorted.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No stages yet. Click "Add Stage" to create one.
        </Typography>
      )}

      {sorted.map((stage, index) => {
        const draft = getDraft(stage);
        const isUpdating = updateSkill.isPending;
        const isUploading = uploadLessonPlan.isPending && uploadingSkillId === stage.id;

        return (
          <Box
            key={stage.id}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {/* Row 1: Name + Color + Reorder + Delete */}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label={`Stage ${index + 1}`}
                value={draft.name}
                onChange={(e) => setDraft(stage.id, { name: e.target.value })}
                onBlur={() => saveNameColor(stage)}
                size="small"
                fullWidth
                disabled={isUpdating}
              />
              <ColorPickerField
                label=""
                value={draft.color ?? null}
                onChange={(color) => {
                  // Get current name from state/fallback before setting new draft
                  const currentName = editState[stage.id]?.name ?? stage.name;
                  setDraft(stage.id, { color });
                  // Persist the color immediately (no separate blur needed)
                  updateSkill.mutate({
                    skillId: stage.id,
                    data: { name: currentName || stage.name, color },
                  });
                }}
                showClear
                useSchoolPalette
              />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Tooltip title="Move up">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => moveStage(index, 'up')}
                      disabled={index === 0 || reorderSkills.isPending}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => moveStage(index, 'down')}
                      disabled={index === sorted.length - 1 || reorderSkills.isPending}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Tooltip title="Delete stage">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeleteTarget(stage)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Save name/color button (explicit save as backup for accessibility) */}
            {(draft.name !== stage.name || draft.color !== stage.color) && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => saveNameColor(stage)}
                  disabled={isUpdating}
                >
                  Save
                </Button>
              </Box>
            )}

            {/* Row 2: Lesson Plan */}
            <Stack direction="row" spacing={1} alignItems="center">
              {stage.lessonPlanUrl ? (
                <>
                  <PictureAsPdfIcon fontSize="small" color="error" />
                  <Typography
                    variant="caption"
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {stage.lessonPlanName || stage.lessonPlanUrl}
                  </Typography>
                  <Tooltip title="Open lesson plan">
                    <IconButton
                      size="small"
                      onClick={() => window.open(stage.lessonPlanUrl!, '_blank', 'noopener,noreferrer')}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove lesson plan">
                    <IconButton
                      size="small"
                      onClick={() => handleClearLessonPlan(stage.id)}
                      disabled={clearLessonPlan.isPending}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  No lesson plan
                </Typography>
              )}
              <Tooltip title={driveConnected ? 'Browse Google Drive' : 'Connect Google Drive to attach lesson plans'}>
                <span>
                  <Button
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setDrivePickerSkillId(stage.id)}
                    disabled={!driveConnected}
                  >
                    Browse
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={driveConnected ? 'Upload PDF' : 'Connect Google Drive to upload'}>
                <span>
                  <Button
                    size="small"
                    onClick={() => triggerUpload(stage.id)}
                    disabled={!driveConnected || isUploading}
                    startIcon={isUploading ? <CircularProgress size={14} /> : undefined}
                  >
                    Upload
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        );
      })}

      {/* Add Stage */}
      <Button
        variant="outlined"
        startIcon={addSkill.isPending ? <CircularProgress size={16} /> : <AddIcon />}
        onClick={handleAddStage}
        disabled={addSkill.isPending}
        size="small"
        sx={{ alignSelf: 'flex-start' }}
      >
        Add Stage
      </Button>

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Stage"
        message={`Delete stage "${deleteTarget?.name}"? This will also delete all student progress for this stage and cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteSkill.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Drive File Picker (shared, one at a time) */}
      <DriveFilePicker
        open={!!drivePickerSkillId}
        onClose={() => setDrivePickerSkillId(null)}
        onSelect={handleDriveSelect}
        title="Select Lesson Plan"
        fileTypes={['pdf']}
      />

      {/* Hidden file input for upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </Box>
  );
}
